import React, { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { ChargeListItem } from '../types';
import { countryLabel } from './CountrySelect';
import SortableHeader from './SortableHeader';
import ZollIncompleteMark from './ZollIncompleteMark';
import { tokens } from '../theme';
import type { SortDirection } from '../utils/tableSort';
import { useVisibleRange } from '../utils/useVisibleRange';

export type ChargeSortColumn =
  | 'charge_number'
  | 'b_nummer'
  | 'country'
  | 'melt_country'
  | 'certificate_number'
  | 'lieferant'
  | 'materialnummer'
  | 'coil_size'
  | 'coil_count'
  | 'creator'
  | 'created_at';

interface ChargeResultTableProps {
  rows: ChargeListItem[];
  sort: ChargeSortColumn;
  order: SortDirection;
  onSort: (column: ChargeSortColumn) => void;
  canWrite: boolean;
  busy: boolean;
  onEdit: (charge: ChargeListItem) => void;
  onDelete: (charge: ChargeListItem) => void;
  onLabels: (charge: ChargeListItem) => void;
  onZeugnis: (charge: ChargeListItem) => void;
  onHistory: (charge: ChargeListItem) => void;
}

const COLUMN_COUNT = 12;
const VIRTUAL_AFTER = 40;
const INITIAL_ROW_HEIGHT = 41;

function ChargeResultTable({
  rows,
  sort,
  order,
  onSort,
  canWrite,
  busy,
  onEdit,
  onDelete,
  onLabels,
  onZeugnis,
  onHistory,
}: ChargeResultTableProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [rowHeight, setRowHeight] = useState(INITIAL_ROW_HEIGHT);
  const virtual = rows.length > VIRTUAL_AFTER;
  const range = useVisibleRange(virtual ? rows.length : 0, rowHeight, bodyRef);
  const start = virtual ? range.start : 0;
  const end = virtual ? range.end : rows.length;
  const visible = virtual ? rows.slice(start, end) : rows;

  const measureRow = (node: HTMLTableRowElement | null) => {
    if (!node || !virtual) return;
    const height = Math.round(node.getBoundingClientRect().height);
    if (height > 20 && Math.abs(height - rowHeight) > 1) setRowHeight(height);
  };

  return (
    <Paper aria-busy={busy}>
      <TableContainer>
        <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap', py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <SortableHeader column="charge_number" label="Charge-Nr." sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="b_nummer" label="B-Nummer" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="country" label="Ursprung" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="melt_country" label="Schmelzland" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="lieferant" label="Lieferant" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="coil_size" label="Größe" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="certificate_number" label="Lieferanten Zertifikat" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="materialnummer" label="Material Lexware" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="coil_count" label="Anzahl Coils" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="creator" label="Ersteller" sort={sort} order={order} onSort={onSort} />
              <SortableHeader column="created_at" label="Erstellt am" sort={sort} order={order} onSort={onSort} />
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody ref={bodyRef}>
            {virtual && start > 0 && (
              <TableRow aria-hidden>
                <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 0, lineHeight: 0 }}>
                  <div style={{ height: start * rowHeight }} />
                </TableCell>
              </TableRow>
            )}
            {visible.map((charge, index) => (
              <TableRow key={charge.id} ref={index === 0 ? measureRow : undefined}>
                <TableCell sx={{ fontWeight: 500 }}>{charge.charge_number}</TableCell>
                <TableCell>{charge.b_nummer || '—'}</TableCell>
                <TableCell>
                  {charge.country_name
                    ? countryLabel({
                        name: charge.country_name,
                        code: charge.country_code || '',
                        id: 0,
                        created_at: '',
                      })
                    : '—'}
                </TableCell>
                <TableCell>
                  {charge.melt_country_name
                    ? countryLabel(
                        {
                          name: charge.melt_country_name,
                          code: charge.melt_country_code || '',
                          melt_pour_code: charge.melt_pour_code,
                          id: 0,
                          created_at: '',
                        },
                        true,
                      )
                    : '—'}
                </TableCell>
                <TableCell>{charge.lieferant_name ?? '-'}</TableCell>
                <TableCell>
                  {charge.coil_dicke} × {charge.coil_breite} mm
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {charge.certificate_number}
                    {charge.certificate_blocked && (
                      <Typography variant="body2" color="warning.main" noWrap sx={{ fontSize: 12 }}>
                        Unvollständig
                      </Typography>
                    )}
                    {charge.zoll_incomplete && <ZollIncompleteMark />}
                    {charge.out_of_range_count > 0 && (
                      <Tooltip title="Im Lieferanten Zertifikat liegt ein Wert außerhalb der Vorgabe">
                        <Box
                          component={RouterLink}
                          to={`/certificates/${charge.certificate_id}`}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            borderRadius: 999,
                            px: 1,
                            py: 0.15,
                            bgcolor: tokens.badSoft,
                            color: tokens.bad,
                            textDecoration: 'none',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {charge.out_of_range_count} außerhalb
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{charge.materialnummer}</TableCell>
                <TableCell>
                  {charge.coils && charge.coils.length > 0 ? (
                    <Tooltip
                      title={
                        <Stack spacing={0.25}>
                          {charge.coils.map((coil) => (
                            <Typography key={coil.id} variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {coil.barcode}
                            </Typography>
                          ))}
                        </Stack>
                      }
                      placement="top"
                      arrow
                    >
                      <Box component="span" sx={{ cursor: 'help' }}>
                        {charge.coils.length}
                      </Box>
                    </Tooltip>
                  ) : (
                    '—'
                  )}
                </TableCell>
                <TableCell>{charge.creator_full_name}</TableCell>
                <TableCell>{new Date(charge.created_at).toLocaleDateString('de-DE')}</TableCell>
                <TableCell align="right">
                  {canWrite && (
                    <IconButton size="small" onClick={() => onEdit(charge)} aria-label="Charge bearbeiten">
                      <EditIcon />
                    </IconButton>
                  )}
                  <Tooltip title="Etiketten drucken">
                    <IconButton size="small" onClick={() => onLabels(charge)} aria-label="Etiketten drucken">
                      <LocalPrintshopIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      charge.certificate_blocked
                        ? 'Lieferanten Zertifikat ist noch unvollständig. Druck erst nach dem vollständigen Speichern.'
                        : 'Kalotte-Zeugnis erstellen'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!!charge.certificate_blocked}
                        onClick={() => onZeugnis(charge)}
                        aria-label="Kalotte-Zeugnis erstellen"
                      >
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Zeugnis-Historie">
                    <IconButton
                      size="small"
                      color={charge.zeugnis_issued_at ? 'primary' : 'default'}
                      onClick={() => onHistory(charge)}
                      aria-label="Zeugnis-Historie"
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canWrite && (
                    <IconButton size="small" color="error" onClick={() => onDelete(charge)} aria-label="Charge löschen">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {virtual && end < rows.length && (
              <TableRow aria-hidden>
                <TableCell colSpan={COLUMN_COUNT} sx={{ p: 0, border: 0, lineHeight: 0 }}>
                  <div style={{ height: (rows.length - end) * rowHeight }} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default React.memo(ChargeResultTable);
